import { DEVICE_BREAKPOINT } from '@/plugin/device-breakpoint'
import { defineStore } from 'pinia'
import { computed, inject, ref, watch } from 'vue'
import router from '@/router'

export type SidebarPosition = 'left' | 'right'

export const useSidebarStateStore = defineStore('sidebar-state', () => {

  const { isTablet, isDesktop } = inject(DEVICE_BREAKPOINT)!

  const isReducedTablet = ref(true)
  const isReducedDesktop = ref(false)

  const mobileSidebars = ref<Record<SidebarPosition, boolean>>({
    left: false,
    right: false,
  })

  const isOpenMobileSidebarLeft = computed(() => mobileSidebars.value.left)
  const isOpenMobileSidebarRight = computed(() => mobileSidebars.value.right)

  const resetMobile = () => {
    mobileSidebars.value.left = false
    mobileSidebars.value.right = false
  }

  const setMobileSidebar = (position: SidebarPosition, open: boolean) => {
    resetMobile()
    if (open) mobileSidebars.value[position] = true
  }

  const toggleMobileSidebar = (position: SidebarPosition) => setMobileSidebar(position, !mobileSidebars.value[position])

  const openMobileSidebar = (position: SidebarPosition) => setMobileSidebar(position, true)

  const closeMobileSidebar = (position: SidebarPosition) => {
    mobileSidebars.value[position] = false
  }

  const isSidebarReduced = computed({
    get: () => (isTablet.value ? isReducedTablet.value : isReducedDesktop.value),
    set: (value: boolean) => {
      isReducedTablet.value = value
      isReducedDesktop.value = value
      if (value) closeMobileSidebar('left')
      else openMobileSidebar('left')
    },
  })

  function toggleSidebar() {
    isSidebarReduced.value = !isSidebarReduced.value
  }

  function onClickOutside() {
    if (!isDesktop.value) closeMobileSidebar('left')
  }

  const sidebarItems = computed(() => {
    return router
      .getRoutes()
      .filter((route) => {
        if (!route.meta.label || !route.meta.icon) return false

        // Gestion de la visibility
        let visibility = route.meta.visibility
        if (typeof visibility === 'function') {
          visibility = visibility(route)
        }

        return visibility !== 'hidden'
      })
      .sort((a, b) => (Number(a.meta.order) || 0) - (Number(b.meta.order) || 0))
      .map((route) => {
        return route
      })
  })

  watch(
    () => mobileSidebars.value.left,
    (isOpen) => {
      isSidebarReduced.value = !isOpen
    },
  )

  return {
    isSidebarReduced,
    isOpenMobileSidebarLeft,
    isOpenMobileSidebarRight,
    toggleSidebar,
    onClickOutside,
    toggleMobileSidebar,
    openMobileSidebar,
    closeMobileSidebar,
    sidebarItems,
  }
})
